import * as XLSX from 'xlsx';
import { GameHistoryItem, PlayerRoundRecord } from '../modules/stats/types';
import { DojosRow, SeasonsRow } from '../types/database';
import { ROLE_NAMES, VictoryType, Faction, PlayerRole } from '../types/stats';
import { supabase } from '../lib/supabase';

// V131: 精简导出字段接口
export interface ExportRow {
  对局ID: number;
  日期: string;
  道场: string;
  赛季: string;
  玩家昵称: string;
  角色: string;
  胜利方式: string;
  任务进程: string;
  MVP: string;
  积分变化: number;
}

// V131: 导入模板字段（无积分变化、阵营、胜负）
export interface ImportTemplateRow {
  对局ID: number;
  日期: string;
  道场: string;
  赛季: string;
  玩家昵称: string;
  角色: string;
  胜利方式: string;
  任务进程: string;
  MVP: string;
}

// V131: 胜利方式选项
export const VICTORY_WAY_OPTIONS = [
  { value: 'mission_good', label: '完成任务梅林隐藏（正义胜）', faction: 'good' as Faction },
  { value: 'assassin_fail', label: '提前刺杀失败（正义胜）', faction: 'good' as Faction },
  { value: 'sabotage', label: '破坏三次任务（邪恶胜）', faction: 'evil' as Faction },
  { value: 'assassin_success', label: '刺杀梅林成功（邪恶胜）', faction: 'evil' as Faction },
];

// V131: 胜利方式映射
export const VICTORY_WAY_MAP: Record<string, { victoryType: VictoryType; winningFaction: Faction }> = {
  '完成任务梅林隐藏（正义胜）': { victoryType: 'mission', winningFaction: 'good' },
  '提前刺杀失败（正义胜）': { victoryType: 'assassin_fail', winningFaction: 'good' },
  '破坏三次任务（邪恶胜）': { victoryType: 'sabotage', winningFaction: 'evil' },
  '刺杀梅林成功（邪恶胜）': { victoryType: 'assassin_success', winningFaction: 'evil' },
};

// V131: 角色阵营映射（用于自动判定）
export const ROLE_TO_FACTION: Record<string, Faction> = {
  '梅林': 'good',
  '派西维尔': 'good',
  '忠臣': 'good',
  '好兰斯洛特': 'good',
  '莫德雷德': 'evil',
  '莫甘娜': 'evil',
  '刺客': 'evil',
  '爪牙': 'evil',
  '坏兰斯洛特': 'evil',
  '奥伯伦': 'evil',
};

// V131: 反向角色名称映射
export const REVERSE_ROLE_NAMES: Record<string, PlayerRole> = {
  '梅林': 'merlin',
  '派西维尔': 'percival',
  '忠臣': 'loyal',
  '好兰斯洛特': 'lancelot_good',
  '莫德雷德': 'mordred',
  '莫甘娜': 'morgana',
  '刺客': 'assassin',
  '爪牙': 'minion',
  '坏兰斯洛特': 'lancelot_good', // 注意：需要特殊处理
  '奥伯伦': 'oberon',
};

// V131: 导入校验结果
export interface ImportValidationResult {
  isValid: boolean;
  row: number;
  errors: string[];
  data?: Partial<ImportTemplateRow>;
}

// V131: 导入处理结果
export interface ImportProcessResult {
  total: number;
  success: number;
  failed: number;
  errors: { row: number; errors: string[] }[];
}

/**
 * V131: 解析任务进程字符串
 * @param missionStr 如"蓝红蓝红蓝"
 * @returns 任务结果数组，true=成功，false=失败，null=未进行
 */
export function parseMissionProgress(missionStr: string): (boolean | null)[] {
  const results: (boolean | null)[] = [];
  for (const char of missionStr) {
    if (char === '蓝') results.push(true);
    else if (char === '红') results.push(false);
    else results.push(null);
  }
  // 补齐到5个
  while (results.length < 5) results.push(null);
  return results.slice(0, 5);
}

/**
 * V131: 验证任务进程与胜利方式的合理性
 */
export function validateMissionVictory(missionStr: string, victoryWay: string): { valid: boolean; message?: string } {
  const missionResults = parseMissionProgress(missionStr);
  const successCount = missionResults.filter(r => r === true).length;
  const failCount = missionResults.filter(r => r === false).length;
  
  const victoryInfo = VICTORY_WAY_MAP[victoryWay];
  if (!victoryInfo) {
    return { valid: false, message: '未知的胜利方式' };
  }
  
  // 正义胜利需要至少3次成功
  if (victoryInfo.winningFaction === 'good' && successCount < 3) {
    return { valid: false, message: `正义胜利需要至少3次任务成功，当前仅${successCount}次` };
  }
  
  // 邪恶胜利需要至少3次失败
  if (victoryInfo.winningFaction === 'evil' && failCount < 3) {
    return { valid: false, message: `邪恶胜利需要至少3次任务失败，当前仅${failCount}次` };
  }
  
  return { valid: true };
}

/**
 * V131: 智能计算积分变化
 * 根据胜负、MVP、角色等自动计算
 */
export function calculateScoreChange(
  isWin: boolean,
  isMVP: boolean,
  role: string,
  _playerFaction: Faction,
  _winningFaction: Faction
): number {
  let score = 0;
  
  // 基础胜负分
  if (isWin) {
    score += 10; // 胜利基础分
  } else {
    score -= 5; // 失败扣分
  }
  
  // MVP加分
  if (isMVP) {
    score += 5;
  }
  
  // 角色特殊加分
  if (role === '梅林' && isWin) score += 2;
  if (role === '刺客' && isWin) score += 2;
  
  return score;
}

/**
 * V131: 导出到 Excel（固定精简字段）
 */
export function exportToExcel(
  games: GameHistoryItem[],
  dojos: DojosRow[],
  seasons: SeasonsRow[],
  loadAll = false
): void {
  const rows: ExportRow[] = [];
  
  games.forEach(game => {
    const dojo = dojos.find(d => d.id === game.dojo_id);
    const season = seasons.find(s => s.id === game.season_id);
    
    // 构建任务进程字符串
    const taskResults = ((game.task_results as unknown) as (boolean | null)[]) || [];
    const missionStr = taskResults.map(r => {
      if (r === true) return '蓝';
      if (r === false) return '红';
      return '-';
    }).join('');
    
    // 构建胜利方式字符串
    let victoryWay = '';
    if (game.victory_type === 'mission') victoryWay = '完成任务梅林隐藏（正义胜）';
    else if (game.victory_type === 'assassin_fail') victoryWay = '提前刺杀失败（正义胜）';
    else if (game.victory_type === 'sabotage') victoryWay = '破坏三次任务（邪恶胜）';
    else if (game.victory_type === 'assassin_success') victoryWay = '刺杀梅林成功（邪恶胜）';
    
    game.players?.forEach(player => {
      rows.push({
        对局ID: game.id,
        日期: game.game_date ? new Date(game.game_date).toISOString().split('T')[0] : '',
        道场: dojo?.name || '',
        赛季: season?.name || '',
        玩家昵称: player.player_nickname || '',
        角色: ROLE_NAMES[player.player_role as PlayerRole] || player.player_role || '',
        胜利方式: victoryWay,
        任务进程: missionStr,
        MVP: player.is_mvp ? '是' : '否',
        积分变化: player.score_change || 0,
      });
    });
  });
  
  // 生成文件名
  const now = new Date();
  const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
  const filename = `Avalon_战绩_${timestamp}.xlsx`;
  
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '战绩数据');
  XLSX.writeFile(wb, filename);
}

/**
 * V131: 导出到 CSV（固定精简字段）
 */
export function exportToCSV(
  games: GameHistoryItem[],
  dojos: DojosRow[],
  seasons: SeasonsRow[],
  loadAll = false
): void {
  const rows: ExportRow[] = [];
  
  games.forEach(game => {
    const dojo = dojos.find(d => d.id === game.dojo_id);
    const season = seasons.find(s => s.id === game.season_id);
    
    const taskResults = ((game.task_results as unknown) as (boolean | null)[]) || [];
    const missionStr = taskResults.map(r => {
      if (r === true) return '蓝';
      if (r === false) return '红';
      return '-';
    }).join('');
    
    let victoryWay = '';
    if (game.victory_type === 'mission') victoryWay = '完成任务梅林隐藏（正义胜）';
    else if (game.victory_type === 'assassin_fail') victoryWay = '提前刺杀失败（正义胜）';
    else if (game.victory_type === 'sabotage') victoryWay = '破坏三次任务（邪恶胜）';
    else if (game.victory_type === 'assassin_success') victoryWay = '刺杀梅林成功（邪恶胜）';
    
    game.players?.forEach(player => {
      rows.push({
        对局ID: game.id,
        日期: game.game_date ? new Date(game.game_date).toISOString().split('T')[0] : '',
        道场: dojo?.name || '',
        赛季: season?.name || '',
        玩家昵称: player.player_nickname || '',
        角色: ROLE_NAMES[player.player_role as PlayerRole] || player.player_role || '',
        胜利方式: victoryWay,
        任务进程: missionStr,
        MVP: player.is_mvp ? '是' : '否',
        积分变化: player.score_change || 0,
      });
    });
  });
  
  const now = new Date();
  const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
  const filename = `Avalon_战绩_${timestamp}.csv`;
  
  const ws = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(ws);
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

/**
 * V133: 下载导入模板（增强示例说明）
 */
export function downloadImportTemplate(): void {
  // V133: 4种胜利方式示例数据
  const exampleRows: ImportTemplateRow[] = [
    // 示例1: 完成任务梅林隐藏（正义胜）
    {
      对局ID: 1001,
      日期: '2024-01-15',
      道场: '测试道场',
      赛季: 'S1赛季',
      玩家昵称: '玩家A',
      角色: '梅林',
      胜利方式: '完成任务梅林隐藏（正义胜）',
      任务进程: '蓝蓝蓝--',
      MVP: '是',
    },
    {
      对局ID: 1001,
      日期: '2024-01-15',
      道场: '测试道场',
      赛季: 'S1赛季',
      玩家昵称: '玩家B',
      角色: '刺客',
      胜利方式: '完成任务梅林隐藏（正义胜）',
      任务进程: '蓝蓝蓝--',
      MVP: '否',
    },
    // 示例2: 提前刺杀失败（正义胜）
    {
      对局ID: 1002,
      日期: '2024-01-16',
      道场: '测试道场',
      赛季: 'S1赛季',
      玩家昵称: '玩家C',
      角色: '派西维尔',
      胜利方式: '提前刺杀失败（正义胜）',
      任务进程: '蓝红蓝蓝-',
      MVP: '否',
    },
    {
      对局ID: 1002,
      日期: '2024-01-16',
      道场: '测试道场',
      赛季: 'S1赛季',
      玩家昵称: '玩家D',
      角色: '莫甘娜',
      胜利方式: '提前刺杀失败（正义胜）',
      任务进程: '蓝红蓝蓝-',
      MVP: '否',
    },
    // 示例3: 破坏三次任务（邪恶胜）
    {
      对局ID: 1003,
      日期: '2024-01-17',
      道场: '测试道场',
      赛季: 'S1赛季',
      玩家昵称: '玩家E',
      角色: '忠臣',
      胜利方式: '破坏三次任务（邪恶胜）',
      任务进程: '红红蓝红-',
      MVP: '否',
    },
    {
      对局ID: 1003,
      日期: '2024-01-17',
      道场: '测试道场',
      赛季: 'S1赛季',
      玩家昵称: '玩家F',
      角色: '莫德雷德',
      胜利方式: '破坏三次任务（邪恶胜）',
      任务进程: '红红蓝红-',
      MVP: '是',
    },
    // 示例4: 刺杀梅林成功（邪恶胜）
    {
      对局ID: 1004,
      日期: '2024-01-18',
      道场: '测试道场',
      赛季: 'S1赛季',
      玩家昵称: '玩家G',
      角色: '梅林',
      胜利方式: '刺杀梅林成功（邪恶胜）',
      任务进程: '蓝蓝蓝--',
      MVP: '否',
    },
    {
      对局ID: 1004,
      日期: '2024-01-18',
      道场: '测试道场',
      赛季: 'S1赛季',
      玩家昵称: '玩家H',
      角色: '刺客',
      胜利方式: '刺杀梅林成功（邪恶胜）',
      任务进程: '蓝蓝蓝--',
      MVP: '是',
    },
  ];
  
  // V133: 创建说明工作表
  const instructionData = [
    ['字段', '说明', '示例'],
    ['对局ID', '对局唯一标识（数字）', '1001'],
    ['日期', '格式：YYYY-MM-DD', '2024-01-15'],
    ['道场', '道场名称', '测试道场'],
    ['赛季', '赛季名称', 'S1赛季'],
    ['玩家昵称', '玩家显示名称', '玩家A'],
    ['角色', '可选：梅林、派西维尔、忠臣、好兰斯洛特、莫德雷德、莫甘娜、刺客、爪牙、坏兰斯洛特、奥伯伦', '梅林'],
    ['胜利方式', '共4种，请根据对局结果选择对应项', '完成任务梅林隐藏（正义胜）'],
    ['任务进程', '5个字符，蓝=成功，红=失败，-=未进行', '蓝蓝蓝--'],
    ['MVP', '是 或 否', '是'],
    ['', '', ''],
    ['胜利方式选项（共4种）', '', ''],
    ['1. 完成任务梅林隐藏（正义胜）', '正义阵营完成3次任务且梅林未被刺杀', ''],
    ['2. 提前刺杀失败（正义胜）', '刺客提前刺杀但未刺中梅林', ''],
    ['3. 破坏三次任务（邪恶胜）', '邪恶阵营破坏三次任务获胜', ''],
    ['4. 刺杀梅林成功（邪恶胜）', '刺客成功刺杀梅林获胜', ''],
    ['', '', ''],
    ['任务进程填写示例（6种常见案例）', '', ''],
    ['【蓝红蓝蓝-】', '5轮任务，第1/3/4轮成功，第2轮失败，第5轮未进行', ''],
    ['【蓝蓝蓝--】', '前3轮连续成功达成胜利，后2轮无需进行', ''],
    ['【蓝红红蓝红】', '5轮完整进行，3次成功2次失败', ''],
    ['【蓝----】', '仅第1轮成功，后续未进行（可能提前结束的对局）', ''],
    ['【红红----】', '前2轮失败，后续未进行', ''],
    ['【-----】', '5轮均未进行或未记录（特殊对局）', ''],
  ];
  
  const wb = XLSX.utils.book_new();
  
  // 说明工作表
  const wsInstruction = XLSX.utils.aoa_to_sheet(instructionData);
  XLSX.utils.book_append_sheet(wb, wsInstruction, '填写说明');
  
  // 示例数据工作表
  const wsData = XLSX.utils.json_to_sheet(exampleRows);
  XLSX.utils.book_append_sheet(wb, wsData, '示例数据');
  
  XLSX.writeFile(wb, 'Avalon_战绩导入模板_V133.xlsx');
}

/**
 * V131: 解析导入文件
 */
export function parseImportFile(file: File): Promise<Partial<ImportTemplateRow>[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as string[][];
        
        if (jsonData.length < 2) {
          reject(new Error('文件数据不足'));
          return;
        }
        
        // 跳过说明行（第一行是说明，第二行是示例或数据）
        const startRow = jsonData[0][0]?.toString().includes('说明') || 
                        jsonData[0][0]?.toString().includes('格式') ? 2 : 1;
        
        const headers = jsonData[0];
        const rows: Partial<ImportTemplateRow>[] = [];
        
        for (let i = startRow; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row[0]) continue; // 跳过空行
          
          const rowData: Partial<ImportTemplateRow> = {};
          headers.forEach((header, index) => {
            const value = row[index];
            switch (header) {
              case '对局ID':
                rowData.对局ID = value ? Number(value) : undefined;
                break;
              case '日期':
                rowData.日期 = value?.toString() || '';
                break;
              case '道场':
                rowData.道场 = value?.toString() || '';
                break;
              case '赛季':
                rowData.赛季 = value?.toString() || '';
                break;
              case '玩家昵称':
                rowData.玩家昵称 = value?.toString() || '';
                break;
              case '角色':
                rowData.角色 = value?.toString() || '';
                break;
              case '胜利方式':
                rowData.胜利方式 = value?.toString() || '';
                break;
              case '任务进程':
                rowData.任务进程 = value?.toString() || '';
                break;
              case 'MVP':
                rowData.MVP = value?.toString() || '';
                break;
            }
          });
          
          rows.push(rowData);
        }
        
        resolve(rows);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('文件读取失败'));
    reader.readAsBinaryString(file);
  });
}

/**
 * V131: 校验导入数据（智能计算前）
 */
export function validateImportData(rows: Partial<ImportTemplateRow>[]): ImportValidationResult[] {
  return rows.map((row, index) => {
    const errors: string[] = [];
    const rowNum = index + 1;
    
    // 必填字段检查
    if (!row.对局ID) errors.push('对局ID为空');
    if (!row.日期) errors.push('日期为空');
    if (!row.玩家昵称) errors.push('玩家昵称为空');
    if (!row.角色) errors.push('角色为空');
    if (!row.胜利方式) errors.push('胜利方式为空');
    if (!row.任务进程) errors.push('任务进程为空');
    
    // 角色有效性检查
    const validRoles = Object.keys(ROLE_TO_FACTION);
    if (row.角色 && !validRoles.includes(row.角色)) {
      errors.push(`角色"${row.角色}"无效，可选：${validRoles.join('、')}`);
    }
    
    // 胜利方式有效性检查
    if (row.胜利方式 && !VICTORY_WAY_MAP[row.胜利方式]) {
      errors.push(`胜利方式"${row.胜利方式}"无效`);
    }
    
    // 任务进程格式检查
    if (row.任务进程) {
      if (row.任务进程.length !== 5) {
        errors.push('任务进程必须是5个字符');
      } else if (!/^[蓝红\-]+$/.test(row.任务进程)) {
        errors.push('任务进程只能包含"蓝"、"红"或"-"');
      }
    }
    
    // 任务进程与胜利方式一致性检查
    if (row.胜利方式 && row.任务进程) {
      const validation = validateMissionVictory(row.任务进程, row.胜利方式);
      if (!validation.valid) {
        errors.push(validation.message || '任务进程与胜利方式不匹配');
      }
    }
    
    // MVP格式检查
    if (row.MVP && !['是', '否'].includes(row.MVP)) {
      errors.push('MVP必须是"是"或"否"');
    }
    
    return {
      isValid: errors.length === 0,
      row: rowNum,
      errors,
      data: row,
    };
  });
}

/**
 * V131: 转换导入数据为游戏数据（智能计算积分、阵营、胜负）
 */
export function convertImportToGameData(
  rows: ImportTemplateRow[]
): { games: Partial<GameHistoryItem>[]; count: number } {
  const games: Partial<GameHistoryItem>[] = [];
  const gameMap = new Map<string, Partial<GameHistoryItem>>();
  
  rows.forEach(row => {
    const gameKey = `${row.对局ID}_${row.日期}_${row.道场}`;
    
    if (!gameMap.has(gameKey)) {
      // 解析胜利方式
      const victoryInfo = VICTORY_WAY_MAP[row.胜利方式];
      const taskResults = parseMissionProgress(row.任务进程);
      
      gameMap.set(gameKey, {
        id: row.对局ID,
        game_date: row.日期,
        game_number: 1,
        dojo_id: null, // 需要通过名称查找
        season_id: null, // 需要通过名称查找
        player_count: 0, // 稍后统计
        winning_faction: victoryInfo.winningFaction,
        victory_type: victoryInfo.victoryType,
        task_results: taskResults as unknown as Record<string, unknown>,
        players: [],
      });
    }
    
    const game = gameMap.get(gameKey)!;
    
    // 自动判定阵营
    const playerFaction = ROLE_TO_FACTION[row.角色];
    
    // 自动推导胜负
    const victoryInfo = VICTORY_WAY_MAP[row.胜利方式];
    const isWin = playerFaction === victoryInfo.winningFaction;
    const isMVP = row.MVP === '是';
    
    // 智能计算积分
    const scoreChange = calculateScoreChange(isWin, isMVP, row.角色, playerFaction, victoryInfo.winningFaction);
    
    const playerRecord: PlayerRoundRecord = {
      id: 0,
      game_record_id: null,
      player_number: (game.players?.length || 0) + 1,
      player_nickname: row.玩家昵称,
      player_role: REVERSE_ROLE_NAMES[row.角色] || null,
      faction: playerFaction,
      team_type: playerFaction === 'good' ? '正义' : '邪恶',
      score_change: scoreChange,
      is_mvp: isMVP,
      score_id: null,
      base_score: isWin ? 10 : -5,
      mvp_bonus: isMVP ? 5 : 0,
      penalty: 0,
      total_score: scoreChange,
      is_good_mvp: isMVP && playerFaction === 'good',
      is_evil_mvp: isMVP && playerFaction === 'evil',
      has_penalty: false,
      penalty_type: null,
      penalty_score: 0,
      status: 'completed',
      created_at: new Date().toISOString(),
    } as PlayerRoundRecord;
    
    game.players?.push(playerRecord);
    if (game.player_count !== undefined) {
      game.player_count = (game.players?.length || 0);
    }
  });
  
  games.push(...Array.from(gameMap.values()));
  
  return { games, count: games.length };
}

/**
 * V131: 检测文件类型
 */
export function detectFileType(file: File): 'excel' | 'csv' | 'unknown' {
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension === 'xlsx' || extension === 'xls') {
    return 'excel';
  } else if (extension === 'csv') {
    return 'csv';
  }
  return 'unknown';
}
